import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Facebook, CheckCircle2, XCircle, AlertCircle, ExternalLink, RefreshCw, Save, Eye, EyeOff, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { api, BASE } from "../../services/api";

const FB_BLUE = "#1877F2";

function StatusBadge({ status }) {
    if (status === "ok")
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">
                <CheckCircle2 className="w-3 h-3" /> Pripojené
            </span>
        );
    if (status === "error")
        return (
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20">
                <XCircle className="w-3 h-3" /> Chyba
            </span>
        );
    return (
        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-zinc-500/10 text-zinc-500 border border-zinc-700">
            <AlertCircle className="w-3 h-3" /> Nenastavené
        </span>
    );
}

function PostPreview({ post }) {
    return (
        <a href={post.url} target="_blank" rel="noopener noreferrer"
            className="flex gap-3 p-3 rounded-xl border border-white/6 hover:border-white/14 transition-colors group">
            {post.image && (
                <img src={post.image} alt="" className="w-16 h-12 object-cover rounded-lg shrink-0" />
            )}
            <div className="flex-1 min-w-0">
                <p className="text-zinc-300 text-xs leading-relaxed line-clamp-2">
                    {post.message || <span className="text-zinc-600 italic">Bez textu</span>}
                </p>
                <div className="mt-1 flex items-center gap-2 text-zinc-600 text-[10px] font-mono">
                    <span>{new Date(post.date).toLocaleDateString("sk-SK")}</span>
                    <ExternalLink className="w-2.5 h-2.5 group-hover:text-white transition-colors" />
                </div>
            </div>
        </a>
    );
}

export default function AdminFacebook() {
    const [settings, setSettings] = useState({ page_id: "", access_token: "", page_name: "", page_url: "", access_token_masked: "" });
    const [token, setToken] = useState("");
    const [showToken, setShowToken] = useState(false);
    const [saving, setSaving] = useState(false);
    const [testing, setTesting] = useState(false);
    const [testResult, setTestResult] = useState(null);
    const [posts, setPosts] = useState([]);
    const [loadingPosts, setLoadingPosts] = useState(false);
    const [showGuide, setShowGuide] = useState(false);

    useEffect(() => {
        api.getFbSettings().then(s => {
            setSettings(s || {});
            setToken("");
        }).catch(() => {});
    }, []);

    const save = async () => {
        setSaving(true);
        try {
            await api.updateFbSettings({
                page_id:      settings.page_id,
                access_token: token, // empty = keep existing
                page_name:    settings.page_name,
                page_url:     settings.page_url,
            });
            toast.success("Nastavenia uložené");
            setToken("");
            const updated = await api.getFbSettings();
            setSettings(updated || {});
        } catch (e) {
            toast.error(e.message || "Chyba pri ukladaní");
        } finally {
            setSaving(false);
        }
    };

    const test = async () => {
        setTesting(true);
        setTestResult(null);
        try {
            const r = await api.testFbConnection();
            setTestResult({ ok: true, msg: `Nájdených ${r.posts_found} príspevkov` });
            toast.success("Spojenie funguje!");
        } catch (e) {
            setTestResult({ ok: false, msg: e.message });
            toast.error(e.message);
        } finally {
            setTesting(false);
        }
    };

    const loadPosts = async () => {
        setLoadingPosts(true);
        try {
            const data = await api.getFacebookPosts();
            setPosts(data || []);
        } catch {
            toast.error("Nepodarilo sa načítať príspevky");
        } finally {
            setLoadingPosts(false);
        }
    };

    const hasCredentials = !!settings.page_id && (!!settings.access_token_masked || !!token);

    return (
        <div className="max-w-2xl space-y-8">
            <div>
                <h1 className="font-heading text-2xl font-black text-white tracking-tight">Facebook integrácia</h1>
                <p className="text-zinc-500 text-sm mt-1">
                    Pripojte svoju Facebook stránku na zobrazenie príspevkov na webe.
                </p>
            </div>

            {/* Status */}
            <div className="flex items-center justify-between p-4 bg-nova-surface/40 border border-white/8 rounded-xl">
                <div className="flex items-center gap-3">
                    <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: "rgba(24,119,242,0.12)", border: "1px solid rgba(24,119,242,0.25)" }}>
                        <Facebook className="w-4 h-4" style={{ color: FB_BLUE }} />
                    </div>
                    <div>
                        <div className="text-white text-sm font-medium">
                            {settings.page_name || "Facebook stránka"}
                        </div>
                        <div className="font-mono text-[10px] text-zinc-600 uppercase tracking-wider">
                            {settings.page_id ? `ID: ${settings.page_id}` : "Stránka nenakonfigurovaná"}
                        </div>
                    </div>
                </div>
                <StatusBadge status={hasCredentials ? (testResult?.ok === false ? "error" : "ok") : "none"} />
            </div>

            {/* Settings form */}
            <div className="space-y-5 p-6 bg-nova-surface/30 border border-white/8 rounded-2xl">
                <h2 className="font-heading font-bold text-white text-base">Nastavenia pripojenia</h2>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Page ID *</label>
                        <input
                            value={settings.page_id}
                            onChange={e => setSettings(s => ({ ...s, page_id: e.target.value }))}
                            placeholder="napr. 123456789012345"
                            className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-nova-cyan/40 font-mono"
                        />
                    </div>
                    <div>
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">Názov stránky</label>
                        <input
                            value={settings.page_name}
                            onChange={e => setSettings(s => ({ ...s, page_name: e.target.value }))}
                            placeholder="napr. KB MUSIC"
                            className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-nova-cyan/40"
                        />
                    </div>
                </div>

                <div>
                    <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500 mb-1.5 block">URL stránky</label>
                    <input
                        value={settings.page_url}
                        onChange={e => setSettings(s => ({ ...s, page_url: e.target.value }))}
                        placeholder="https://www.facebook.com/kbmusic"
                        className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-nova-cyan/40"
                    />
                </div>

                <div>
                    <div className="flex items-center justify-between mb-1.5">
                        <label className="font-mono text-[10px] uppercase tracking-wider text-zinc-500">
                            Page Access Token {settings.access_token_masked && <span className="text-nova-cyan/70">· aktuálny: {settings.access_token_masked}</span>}
                        </label>
                        <button onClick={() => setShowToken(v => !v)} className="text-zinc-600 hover:text-zinc-400 transition-colors">
                            {showToken ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
                        </button>
                    </div>
                    <input
                        type={showToken ? "text" : "password"}
                        value={token}
                        onChange={e => setToken(e.target.value)}
                        placeholder={settings.access_token_masked ? "Nechajte prázdne pre zachovanie aktuálneho tokenu" : "Vložte Page Access Token…"}
                        className="w-full bg-nova-black/50 border border-white/8 rounded-xl px-4 py-2.5 text-white text-sm outline-none focus:border-nova-cyan/40 font-mono"
                    />
                    <p className="mt-1.5 text-zinc-600 text-[11px]">
                        Potrebujete dlhodobý (never-expiring) Page Access Token z Meta for Developers.
                    </p>
                </div>

                <div className="flex gap-3 pt-1">
                    <button
                        onClick={save} disabled={saving}
                        className="flex items-center gap-2 px-5 py-2.5 bg-nova-cyan rounded-lg text-nova-black text-sm font-semibold hover:bg-nova-cyan/90 transition-colors disabled:opacity-50"
                    >
                        <Save className="w-3.5 h-3.5" />
                        {saving ? "Ukladám…" : "Uložiť nastavenia"}
                    </button>
                    <button
                        onClick={test} disabled={testing || !hasCredentials}
                        className="flex items-center gap-2 px-5 py-2.5 border border-white/10 rounded-lg text-zinc-300 text-sm hover:border-white/25 hover:text-white transition-colors disabled:opacity-40"
                    >
                        {testing ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <CheckCircle2 className="w-3.5 h-3.5" />}
                        Otestovať spojenie
                    </button>
                </div>

                {testResult && (
                    <div className={`flex items-center gap-2 text-sm rounded-lg px-4 py-2.5 border ${
                        testResult.ok
                            ? "bg-emerald-500/8 border-emerald-500/20 text-emerald-400"
                            : "bg-red-500/8 border-red-500/20 text-red-400"
                    }`}>
                        {testResult.ok ? <CheckCircle2 className="w-4 h-4 shrink-0" /> : <XCircle className="w-4 h-4 shrink-0" />}
                        {testResult.msg}
                    </div>
                )}
            </div>

            {/* Post preview */}
            {hasCredentials && (
                <div className="space-y-4 p-6 bg-nova-surface/30 border border-white/8 rounded-2xl">
                    <div className="flex items-center justify-between">
                        <h2 className="font-heading font-bold text-white text-base">Náhľad príspevkov</h2>
                        <button
                            onClick={loadPosts} disabled={loadingPosts}
                            className="flex items-center gap-1.5 text-zinc-500 hover:text-white transition-colors text-xs font-mono"
                        >
                            <RefreshCw className={`w-3.5 h-3.5 ${loadingPosts ? "animate-spin" : ""}`} />
                            Načítať
                        </button>
                    </div>
                    {posts.length === 0 && !loadingPosts && (
                        <p className="text-zinc-600 text-sm text-center py-4">Kliknite "Načítať" pre zobrazenie príspevkov.</p>
                    )}
                    {loadingPosts && (
                        <div className="space-y-2">
                            {[0,1,2].map(i => (
                                <div key={i} className="h-16 rounded-xl bg-nova-surface/40 animate-pulse" />
                            ))}
                        </div>
                    )}
                    <div className="space-y-2">
                        {posts.slice(0, 6).map(p => <PostPreview key={p.id} post={p} />)}
                    </div>
                </div>
            )}

            {/* How to get token guide */}
            <div className="border border-white/6 rounded-2xl overflow-hidden">
                <button
                    onClick={() => setShowGuide(v => !v)}
                    className="w-full flex items-center justify-between px-6 py-4 text-left hover:bg-white/3 transition-colors"
                >
                    <span className="text-white text-sm font-medium">Ako získať Page Access Token?</span>
                    {showGuide ? <ChevronUp className="w-4 h-4 text-zinc-500" /> : <ChevronDown className="w-4 h-4 text-zinc-500" />}
                </button>
                {showGuide && (
                    <div className="px-6 pb-6 text-zinc-400 text-sm space-y-4 border-t border-white/6">
                        <ol className="space-y-3 mt-4 list-none">
                            {[
                                ["Vytvorte Facebook App", "Choďte na developers.facebook.com → My Apps → Create App → Business."],
                                ["Pridajte produkt", "V dashboarde vyberte \"Facebook Login\" alebo \"Pages API\"."],
                                ["Získajte User Token", "V Graph API Explorer (developers.facebook.com/tools/explorer) vygenerujte token s oprávneniami: pages_read_engagement, pages_show_list."],
                                ["Predĺžte platnosť", "Krátky user token (1 hod) predĺžte: GET /oauth/access_token?grant_type=fb_exchange_token&... → dostanete token platný 60 dní."],
                                ["Page Access Token", "Z dlhodobého user tokenu získajte page token: GET /me/accounts?access_token={long_lived_token} → skopírujte access_token pri vašej stránke."],
                                ["Never-expiring token", "Page token generovaný z dlhodobého user tokenu nikdy nevyprší — nemusíte ho obnovovať."],
                            ].map(([title, desc], i) => (
                                <li key={i} className="flex gap-3">
                                    <span className="font-mono text-[10px] text-zinc-600 w-5 shrink-0 mt-0.5">{String(i+1).padStart(2,"0")}</span>
                                    <div>
                                        <div className="font-medium text-white text-sm mb-0.5">{title}</div>
                                        <div className="text-zinc-500 text-xs leading-relaxed">{desc}</div>
                                    </div>
                                </li>
                            ))}
                        </ol>
                        <a
                            href="https://developers.facebook.com/tools/explorer"
                            target="_blank" rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 text-xs font-mono text-nova-cyan hover:text-nova-cyan/80 transition-colors"
                        >
                            <ExternalLink className="w-3 h-3" />
                            Otvoriť Graph API Explorer
                        </a>
                    </div>
                )}
            </div>
        </div>
    );
}
